import { ThinkingStepCodes, type ThinkingStepCode } from "#features/conversations/types/ThinkingStep.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useCallback } from "react";

const getSourceTitlesSummary = (sourceTitles?: string[]): string => {
    if (!sourceTitles || sourceTitles.length === 0) {
        return "";
    }

    return `: ${sourceTitles.join(", ")}`;
};

export const useThinkingStepDefaults = () => {
    const { tShared } = useSharedTranslation();

    const getDefaultStepTitle = useCallback((code: ThinkingStepCode, sourceCount?: number): string => {
        switch (code) {
            case ThinkingStepCodes.thinking:
                return tShared("assistantStepThinking");
            case ThinkingStepCodes.searching:
                return tShared("assistantStepSearching");
            case ThinkingStepCodes.toolRunning:
                return tShared("assistantStepToolRunning");
            case ThinkingStepCodes.processingPageAction:
                return tShared("assistantStepProcessingPageAction");
            case ThinkingStepCodes.assistantMessage:
                return tShared("assistantStepWriting");
            case ThinkingStepCodes.compacting:
                return tShared("assistantStepCompacting");
            case ThinkingStepCodes.skillLoaded:
                return tShared("assistantStepSkillLoaded");
            case ThinkingStepCodes.sources:
                return tShared("assistantStepSources", { count: sourceCount ?? 0 });
        }
    }, [tShared]);

    const getDefaultStepDetail = useCallback((code: ThinkingStepCode, sourceCount?: number, sourceTitles?: string[], title?: string): string => {
        const count = sourceCount ?? 0;
        const sourceList = getSourceTitlesSummary(sourceTitles);

        switch (code) {
            case ThinkingStepCodes.thinking:
                return tShared("assistantStepDetailThinking");
            case ThinkingStepCodes.searching:
                if (count > 0) {
                    return tShared("assistantStepDetailSources", { count, sourceList });
                }

                return tShared("assistantStepDetailSearching");
            case ThinkingStepCodes.toolRunning:
                return tShared("assistantStepDetailToolRunning");
            case ThinkingStepCodes.processingPageAction:
                return tShared("assistantStepDetailProcessingPageAction");
            case ThinkingStepCodes.assistantMessage:
                if (count > 0) {
                    return tShared("assistantStepDetailWritingWithSources", { count });
                }

                return tShared("assistantStepDetailWriting");
            case ThinkingStepCodes.compacting:
                return tShared("assistantStepDetailCompacting");
            case ThinkingStepCodes.skillLoaded:
                if (title) {
                    return tShared("assistantStepDetailSkillLoadedNamed", { skill: title });
                }

                return tShared("assistantStepDetailSkillLoaded");
            case ThinkingStepCodes.sources:
                return tShared("assistantStepDetailSources", { count, sourceList });
        }
    }, [tShared]);

    return { getDefaultStepTitle, getDefaultStepDetail };
};
